import { CommonRegexMap, DatastoreObjectType, DeviceTypes, isValidIoTVeui, isValidLoRaAppKey, isValidLoRaEui, MapLike, Networks } from '@iotv/iotv-v3-types'
import { DeviceMandatoryProps, DeviceProps, DeviceProvisioningRecordType, DeviceState, IDevice } from '../../ModelClassInterfaces/DeviceType'
import { Milliseconds } from '../../constants'
import { PossibleBusinessObject, RegionalParameterRevision, RegionalPlan, UserAttributeKeyDatePrecision } from '../../types'
import { UserAttributeDef, UserAttributeDefs } from '../../types/ModelTypes'
import { GeoModelCommon } from '../Geo/GeoModelCommon'
import { IAWOLEnabled } from '../Interfaces/TimeRelated'

const typeName = 'Device'

/** TODO: change when DeviceTypes is changed from an enum to a map constructed from external project config outputs */
let DEVICE_TYPE_DROPPABLE: DeviceTypes[] = [DeviceTypes.GPSMLC, DeviceTypes.EffluentTracker]

type DeviceValidationMap = { [k in keyof Omit<DeviceMandatoryProps, 'appID' | 'state'>]: boolean }

interface DeviceClassFns {
  isLoRaLNSCompatible: (this: Device) => boolean
  validateCompatibility: (this: Device) => DeviceValidationMap
}

export class Device extends GeoModelCommon implements IDevice, IAWOLEnabled, Partial<DeviceMandatoryProps>, DeviceClassFns {
  constructor(ob: Partial<DeviceProps> & Partial<PossibleBusinessObject>) {
    super({ ...ob, type: ob.type ?? typeName })
    for (let key in ob) {
      this[key] = ob[key as keyof DeviceProps]
    }
    this.eui = this.eui?.toUpperCase()
    // if (ob.deviceType) {
    this.deviceDroppable = ob.deviceDroppable || (ob.deviceType ? DEVICE_TYPE_DROPPABLE.includes(ob.deviceType) : false)
    // }
  }

  validateCompatibility() {
    const validationMap: DeviceValidationMap = {
      appEui: isValidLoRaEui(this.appEui),
      appKey: isValidLoRaAppKey(this.appKey),
      /** IoTV requires uppercase EUI for DynamoDB indexing, AWS stores as lowercase in LNS, this is handled in AWS LNS code */
      eui: isValidIoTVeui(this.eui),
      deviceId: !!this.deviceId,
      deviceType: !!this.deviceType,
      networkKey: !!this.networkKey,
    }
    return validationMap
  }

  isLoRaLNSCompatible() {
    return Object.values(this.validateCompatibility()).every((v) => v === true)
  }

  getStateKeys(): string[] {
    let stateAttKeys: string[] = []
    return stateAttKeys
  }
  dailyMessageCount: number | undefined
  dailyMessageHistory: number[] | undefined
  yesterdaysMessageCntTotal: number | undefined
  yesterdaysFCntJumpTotal: number | undefined
  dailyFCntSkip: number | undefined
  fCntSkipHistory: number[] | undefined
  /** the sk of the lns resolved in NetworkData  */
  lns?: string
  _permittedAWOL: number | undefined

  overdue: boolean | undefined
  almanacAge: number | undefined
  version?: string
  regionalPlan?: RegionalPlan
  regionalParameterRevision?: RegionalParameterRevision
  appKey?: string
  appEui?: string | undefined
  situation?: string
  model?: string
  batteryCapacity?: number
  error?: MapLike<0 | 1> | undefined
  deviceTypeName?: string
  deviceType?: DeviceTypes
  deviceDroppable: boolean
  deviceSubType?: string
  minVoltage?: number
  maxVoltage?: number
  lowPowerModeThreshold?: number // Voltage below which device will enter low power mode
  normalPowerModeThreshold?: number // Voltage above which device will leave low power mode
  hardwareVersion?: number
  billingGroup?: string
  deviceId?: string // This can usually just be EUI except for TTNE, which gives you a defined device ID
  nasSourceIp?: string
  appID?: string
  networkKey?: string
  lastGatewayEui?: string
  gatewayChanged?: boolean
  eui?: string
  firmwareLevel?: string
  batteryLevel?: number
  batteryPercentage?: number
  provisioning?: DeviceProvisioningRecordType[]
  state?: DeviceState
  adr?: boolean
  dr?: number
  bw?: number
  sf?: number
  rfChain?: number
  rssi?: number
  snr?: number
  fCnt?: number
  fCntInc?: number
  channel?: number
  hasIssue?: boolean
  farFromBoundary?: boolean
  hasPoorReception?: boolean

  messageJoinCount?: number
  messageUplinkCount?: number
  messageDownlinkCount?: number
  messageFailureRate?: number
  messageLast?: number
  messagePeriodDefault?: number
  messagePeriodActual?: number
  messageMissingAcceptable?: number
  lateCount?: number
  sendIntermittently?: boolean

  isAWOL?: boolean
  lastSeen: number | undefined
  notAWOLFrom?: number | undefined
  lastSendPeriod?: number

  unexpectedStationary: number | undefined

  daysLow?: number

  set permittedAWOL_MS(awolMs: number | undefined) {
    this._permittedAWOL = awolMs
  }

  get permittedAWOL_MS() {
    return this._permittedAWOL
  }

  getIfDeviceDropable(): boolean {
    // generic function to be overwritten on parent objects if they have other criterias

    return this.deviceDroppable
  }

  getDeviceConfigKey(): DatastoreObjectType {
    return { pk: this.sk, sk: `${this.sk}_DeviceConfig` }
  }

  getDeviceHistoryKey(): DatastoreObjectType {
    return { pk: this.sk, sk: `${this.sk}_DeviceHistory` }
  }

  getUserAttributes(): UserAttributeDefs & { [k in keyof DeviceProps]: UserAttributeDef } {
    return {
      eui: {
        type: 'String',
        editable: false,
        required: true,
        label: 'Device EUI',
        name: 'eui',
        validation: { regex: CommonRegexMap.LoRa.eui, pass: true, invalidMessage: 'Device EUI must be 8 hexadecimal bytes' },
        valueFn: (newValue: string | undefined) => (newValue ? newValue.toUpperCase().replace(/\s/g, '') : ''),
        displayFn: (val: string | undefined) => {
          return val?.replace(/([0-9a-fA-F]{2})(?!=(\s|$))/g, '$1 ')?.toUpperCase() ?? ''
        },
      },
      deviceId: {
        type: 'String',
        required: true,
        editable: true,
        label: 'Device ID',
        name: 'deviceId',
        // validation: { regex: '/^\S{4,}/', pass: true, invalidMessage: 'Device Id must be present ' },
      },
      appID: { type: 'String', editable: false, label: 'App ID', name: 'appID' },
      networkKey: { type: 'Enum', enumKv: Networks, editable: false, label: 'Application Server', name: 'networkKey', default: Networks.AWS_LPWAN },
      appEui: {
        type: 'String',
        required: false,
        editable: false,
        label: 'App/Join EUI',
        name: 'appEui',
        validation: { regex: CommonRegexMap.IoTV.eui, pass: true, invalidMessage: 'App/Join EUI must be 8 hexadecimal bytes' },
        /** to do, the validation process should regex on the value of valueFn if present, otherwise can't support pretty hex */
        valueFn: (newValue: string | undefined) => (newValue ? newValue.toUpperCase().replace(/\s/g, '') : ''),
        displayFn: (val: string | undefined) => {
          return val?.replace(/([0-9a-fA-F]{2})(?!=(\s|$))/g, '$1 ')?.toUpperCase() ?? ''
        },
      },
      appKey: {
        type: 'String',
        required: false,
        editable: false,
        label: 'App Key',
        name: 'appKey',
        validation: { regex: CommonRegexMap.LoRa.appKey, pass: true, invalidMessage: 'App Key must be 16 hexadecimal bytes' },
        /** to do, the validation process should regex on the value of valueFn if present, otherwise can't support pretty hex */
        valueFn: (newValue: string | undefined) => (newValue ? newValue.toUpperCase().replace(/\s/g, '') : ''),
        displayFn: (val: string | undefined) => {
          return val?.replace(/([0-9a-fA-F]{2})(?!=(\s|$))/g, '$1 ')?.toUpperCase() ?? ''
        },
      },
      deviceType: {
        type: 'Enum',
        editable: true,
        label: 'Device Type',
        name: 'deviceType',
        enumKv: Object.fromEntries(
          Object.entries(DeviceTypes)
            .filter(([k, v]) => !isNaN(parseInt(k)))
            .map(([k, v]) => [v, parseInt(k)]),
        ) as unknown as { [k: string]: string },
      },
      deviceDroppable: { type: 'Boolean', required: false, editable: false, label: 'Device Droppable', name: 'Device Droppable', default: false },

      deviceSubType: { type: 'String', editable: true, label: 'Device Subtype', name: 'deviceSubType' },
      deviceVersion: { type: 'String', editable: true, label: 'Device Version', name: 'deviceVersion' },
      firmwareLevel: { type: 'String', editable: false, label: 'Firmware Level', name: 'firmwareLevel' },
      ...super.getUserAttributes(),
      name: {
        label: 'Device Name',
        type: 'String',
        editable: true,
        required: true,
      },
      state: {
        type: 'Enum',
        editable: true,
        label: 'State',
        name: 'State',
        enumKv: DeviceState,
        valueFn: (c: any) => DeviceState[c as keyof typeof DeviceState] ?? c,
        default: DeviceState.INVENTORY,
      },
      lastSendPeriod: {
        type: 'Number',
        editable: false,
        label: 'Message Period Actual (min)',
        name: 'lastSendPeriod',
        precision: 2,
        displayFn: (actualValue: number | undefined) => (actualValue ? actualValue / Milliseconds.MINUTE : undefined),
      },
      minVoltage: {
        type: 'Number',
        editable: true,
        label: 'Minimum Battery',
        default: 3.27,
        units: 'volts',
        name: 'minVoltage',
      },
      maxVoltage: {
        type: 'Number',
        editable: true,
        label: 'Maximum Battery',
        default: 4.2,
        units: 'volts',
        name: 'maxVoltage',
      },
      lowPowerModeThreshold: {
        type: 'Number',
        editable: true,
        label: 'Low Battery Threshold',
        default: undefined,
        units: 'volts',
        name: 'maxVoltage',
      },
      normalPowerModeThreshold: {
        type: 'Number',
        editable: true,
        label: 'Normal Battery Threshold',
        default: undefined,
        units: 'volts',
        name: 'maxVoltage',
      },
      lastSeen: {
        type: 'Date',
        editable: false,
        label: 'Last Seen',
        name: 'lastSeen',
        precision: UserAttributeKeyDatePrecision.DateTimeSecs,
        // displayFn: (actualValue: number | undefined) => (actualValue ? toDatePickerFormat(actualValue) : undefined),
        // valueFn: (newValue: number | undefined) => (newValue ? new Date(newValue).valueOf() : undefined),
      },
      isAWOL: { type: 'Boolean', editable: false, label: 'AWOL', name: 'isAWOL' },
      fCnt: { type: 'Number', editable: false, label: 'Framecount', name: 'fCnt' },
      fCntInc: { type: 'Number', editable: false, label: 'Framecount Increment', name: 'fCntInc' },
      customer: {
        type: 'String',
        editable: false,
        label: 'Customer',
        name: 'SUPPRESSED_provisioning',
      },
      regionalPlan: {
        type: 'Enum',
        editable: true,
        label: 'Regional Plan',
        name: 'regionalPlan',
        enumKv: RegionalPlan,
      },
      regionalParameterRevision: {
        type: 'Enum',
        editable: true,
        label: 'Regional Params',
        name: 'regionalParameterRevision',
        enumKv: RegionalParameterRevision,
      },
      _permittedAWOL: {
        type: 'Number',
        editable: true,
        label: 'Max AWOL',
        units: 'minutes',
        name: '_permittedAWOL',
        displayFn: (actualValue: number | undefined) => (actualValue ? actualValue / Milliseconds.MINUTE : undefined),
        valueFn: (newValue: number | undefined) => (newValue ? newValue * Milliseconds.MINUTE : undefined),
      },
      overdue: { type: 'Boolean', editable: false, label: 'Overdue', name: 'Overdue' },
      almanacAge: { type: 'Number', editable: false, label: 'Almanac Age', name: 'Almanac Age' },

      billingGroup: { type: 'String', editable: false, label: 'Billing Group', name: 'billingGroup' },
      batteryCapacity: { type: 'Number', editable: false, label: 'Battery Capacity', units: 'mAh', name: 'batteryCapacity' },
      batteryLevel: { type: 'Number', editable: false, label: 'Battery Level', units: 'volts', name: 'batteryLevel' },
      batteryPercentage: { type: 'Number', editable: false, label: 'Battery', units: '%', name: 'batteryPercentage' },
      provisioning: { type: 'object', editable: false, label: 'Provisioning', name: 'provisioning' },
      //Messages: {type:"UNKNOWN",editable:true,label:"//Messages",name:"//Messages"},
      messageJoinCount: { type: 'Number', editable: false, label: 'Joins', name: 'messageJoinCount' },
      messageUplinkCount: { type: 'Number', editable: false, label: 'Uplinks', name: 'messageUplinkCount' },
      messageDownlinkCount: { type: 'Number', editable: false, label: 'Downlinks', name: 'messageDownlinkCount' },
      messageFailureRate: { type: 'Number', editable: false, label: 'Failure Rate', name: 'messageFailureRate' },
      messageLast: { type: 'Number', editable: false, label: 'Last Message', name: 'messageLast' },
      messagePeriodDefault: { type: 'Number', editable: true, label: 'Default Period', name: 'messagePeriodDefault' },
      messagePeriodActual: { type: 'Number', editable: false, label: 'Actual Period', name: 'messagePeriodActual' },
      dailyMessageCount: { type: 'Number', editable: false, label: 'Daily Message Count', name: 'dailyMessageCount' },
      dailyMessageHistory: { type: 'Number', editable: false, label: 'Daily Message Count History', name: 'dailyMessageHistory' },
      dailyFCntSkip: { type: 'Number', editable: false, label: 'Daily Framecount Skip', name: 'dailyFCntSkip' },
      fCntSkipHistory: { type: 'Number', editable: false, label: 'Framecount Skip History', name: 'fCntSkipHistory' },
      yesterdaysMessageCntTotal: { type: 'Number', editable: false, label: "Yesterday's Message Count", name: 'yesterdaysMessageCntTotal' },
      yesterdaysFCntJumpTotal: { type: 'Number', editable: false, label: "Yesterday's Framecount Skip", name: 'yesterdaysFCntJumpTotal' },
    }
  }
}
